import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import adminOpsService from '../services/adminOpsService';
import {
  LayoutDashboard,
  Brain,
  BookOpen,
  Terminal,
  FileEdit,
  Settings,
  LogOut,
  ChevronDown,
  Shield,
  Menu,
  X,
  Workflow,
  Library,
  FileType2,
  ClipboardList,
  FlaskConical,
  Activity,
  Lock,
  Users,
  Server,
  ArrowLeft,
  Upload,
  AlertTriangle,
  CheckCircle,
  Bot,
  Code2,
  BarChart3,
  Ticket,
  Zap,
  Database,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import FundEliteBrandMark from '../components/branding/FundEliteBrandMark';

/**
 * Admin Layout - Phase 13 Admin Sidebar Control System
 * 
 * Dedicated admin shell for internal AI operations, completely
 * separate from the normal user navigation. Only visible to
 * platform_owner, admin, and staff roles.
 * 
 * Sections:
 * - Overview (Dashboard + System Status)
 * - AI Training (Parser, Prompts, Knowledge Base, Letter Training)
 * - Rules & Methods (Workflow Rules, Method Library)
 * - Content (Template Manager, Review Queue)
 * - Testing (Sandbox Tester)
 * - Access (Permissions)
 */

// Admin-only roles that can access this layout
const ADMIN_ROLES = ['platform_owner', 'platform_admin', 'admin', 'owner', 'specialist'];

const NavItem = ({ icon: Icon, label, to, children, openSection, onSectionToggle, badge }) => {
  const location = useLocation();
  const hasChildren = children && children.length > 0;
  const isOpen = openSection === label;
  const isActive = to 
    ? location.pathname === to 
    : children?.some(c => location.pathname === c.to);

  if (hasChildren) {
    return (
      <div className="mb-1">
        <button
          onClick={() => onSectionToggle(isOpen ? null : label)}
          className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all
            ${isActive 
              ? 'bg-emerald-600/20 text-emerald-300 border-l-2 border-emerald-500' 
              : 'text-slate-400 hover:bg-stone-800 hover:text-slate-200'
            }`}
          data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <div className="flex items-center gap-3">
            <Icon size={18} strokeWidth={1.5} />
            <span>{label}</span>
          </div>
          <div className="flex items-center gap-2">
            {badge && (
              <span className="px-2 py-0.5 text-xs bg-emerald-500 text-white rounded-full font-semibold">{badge}</span>
            )}
            <ChevronDown 
              size={14} 
              className={`transition-transform duration-200 ${isOpen ? 'rotate-0' : '-rotate-90'}`}
            />
          </div>
        </button>
        <div 
          className={`overflow-hidden transition-all duration-200 ease-out ${
            isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="mt-1 ml-4 pl-4 border-l border-slate-700/50">
            {children.map((child, index) => (
              <Link
                key={index}
                to={child.to}
                className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all
                  ${location.pathname === child.to
                    ? 'bg-emerald-600/15 text-emerald-300'
                    : 'text-slate-500 hover:bg-stone-800/50 hover:text-slate-300'
                  }`}
                data-testid={`admin-nav-sub-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <span>{child.label}</span>
                {child.badge && (
                  <span className="px-1.5 py-0.5 text-xs bg-emerald-500/80 text-white rounded-full">{child.badge}</span>
                )}
              </Link>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <Link
      to={to}
      className={`flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all mb-1
        ${isActive 
          ? 'bg-emerald-600/20 text-emerald-300 border-l-2 border-emerald-500' 
          : 'text-slate-400 hover:bg-stone-800 hover:text-slate-200'
        }`}
      data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center gap-3">
        <Icon size={18} strokeWidth={1.5} />
        <span>{label}</span>
      </div>
      {badge && (
        <span className="px-2 py-0.5 text-xs bg-emerald-500 text-white rounded-full font-semibold">{badge}</span>
      )}
    </Link>
  );
};

const AdminLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openSection, setOpenSection] = useState(null);
  const [systemStatus, setSystemStatus] = useState({ overall_status: 'unknown', components: [] });

  // Fetch system status for header indicator
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        adminOpsService.setAuthHeaders(getAuthHeaders());
        const status = await adminOpsService.getSystemStatus();
        setSystemStatus(status);
      } catch (error) {
        // Silently fail - header will show unknown status
        console.error('Failed to fetch system status:', error);
      }
    };
    fetchStatus();
    // Refresh status every 60 seconds
    const interval = setInterval(fetchStatus, 60000);
    return () => clearInterval(interval);
  }, [getAuthHeaders]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Auto-open section based on current route
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/admin/parser') || path.includes('/admin/prompts') || 
        path.includes('/admin/knowledge') || path.includes('/admin/letter-training') ||
        path.includes('/admin/decision-training')) {
      setOpenSection('AI Training');
    } else if (path.includes('/admin/extraction-rules') || path.includes('/admin/routing-rules') || 
               path.includes('/admin/methods') || path.includes('/admin/strategy-mapping') || 
               path.includes('/admin/rule-testing') || path.includes('/admin/workflow') ||
               path.includes('/admin/rules-methods') || path.includes('/admin/five-s') ||
               path.includes('/admin/metro2-intelligence')) {
      setOpenSection('Rules & Methods');
    } else if (path.includes('/admin/templates') || path.includes('/admin/review') || 
               path.includes('/admin/blueprints') || path.includes('/admin/template-editor')) {
      setOpenSection('Content');
    } else if (path.includes('/admin/sandbox') || path.includes('/admin/testing')) {
      setOpenSection('Testing');
    } else if (path.includes('/admin/audit') || path.includes('/admin/2fa') || path.includes('/admin/qa-website-access')) {
      setOpenSection('Security');
    } else if (path.includes('/admin/permissions') || path.includes('/admin/access')) {
      setOpenSection('Access');
    } else if (path.includes('/admin/ai-') || path.includes('/admin/dev-agent')) {
      setOpenSection('AI Operations');
    } else if (path.includes('/admin/data-providers')) {
      setOpenSection('Data Providers');
    }
  }, [location.pathname]);

  // Navigation structure
  const navItems = [
    { 
      icon: LayoutDashboard, 
      label: 'Admin Overview', 
      to: '/admin',
    },
    {
      icon: Upload,
      label: 'Training Intake',
      to: '/admin/training-intake',
    },
    {
      icon: Database,
      label: 'Data Providers',
      children: [
        { label: 'DataImpulse Provider', to: '/admin/data-providers/dataimpulse-provider' },
        { label: '911Proxy Provider', to: '/admin/data-providers/911proxy-provider' },
        { label: 'DataImpulse Location Catalog', to: '/admin/data-providers/dataimpulse-location-catalog' },
        { label: 'QA Proxy Policy', to: '/admin/data-providers/qa-proxy-policy' },
      ],
    },
    {
      icon: Brain,
      label: 'AI Training',
      children: [
        { label: 'Decision Training', to: '/admin/decision-training', badge: 'P5' },
        { label: 'Letter Training', to: '/admin/letter-training-mgmt', badge: 'P5' },
        { label: 'Parser Training', to: '/admin/parser-training' },
        { label: 'Prompt Management', to: '/admin/prompts' },
        { label: 'AI Knowledge Base', to: '/admin/knowledge-base' },
        { label: 'Letter Examples (Legacy)', to: '/admin/letter-training' },
      ],
    },
    {
      icon: Workflow,
      label: 'Rules & Methods',
      children: [
        { label: 'Global Output Policy', to: '/admin/rules-methods', badge: 'NEW' },
        { label: 'Metro 2 Intelligence', to: '/admin/metro2-intelligence', badge: 'NEW' },
        { label: 'Five S Classification', to: '/admin/five-s-classification' },
        { label: 'Extraction Rules', to: '/admin/extraction-rules' },
        { label: 'Routing Rules', to: '/admin/routing-rules' },
        { label: 'Method Library', to: '/admin/methods' },
        { label: 'Strategy Mapping', to: '/admin/strategy-mapping' },
        { label: 'Rule Testing (Legacy)', to: '/admin/rule-testing' },
      ],
    },
    {
      icon: FileType2,
      label: 'Content',
      children: [
        { label: 'Template Editor', to: '/admin/template-editor', badge: 'NEW' },
        { label: 'Blueprint CMS', to: '/admin/blueprints' },
        { label: 'Template Manager', to: '/admin/templates' },
        { label: 'Review Queue', to: '/admin/review-queue' },
      ],
    },
    {
      icon: FlaskConical,
      label: 'Testing',
      children: [
        { label: 'Rule Testing', to: '/admin/testing', badge: 'NEW' },
        { label: 'Sandbox Tester', to: '/admin/sandbox' },
      ],
    },
    {
      icon: Server,
      label: 'System Status',
      to: '/admin/system-status',
    },
    {
      icon: Shield,
      label: 'Security',
      children: [
        { label: 'Audit Log', to: '/admin/audit-log' },
        { label: '2FA Settings', to: '/admin/2fa-settings' },
        { label: 'QA Website Access', to: '/admin/qa-website-access' },
      ],
    },
    {
      icon: Lock,
      label: 'Access / Permissions',
      to: '/admin/permissions',
    },
    {
      icon: Zap,
      label: 'AI Operations',
      children: [
        { label: 'Overview', to: '/admin/ai-operations' },
        { label: 'AI Assistant', to: '/admin/ai-assistant' },
        { label: 'Command Center', to: '/admin/dev-agent', badge: 'NEW' },
        { label: 'Training Library', to: '/admin/ai-training' },
        { label: 'Usage & Cost', to: '/admin/ai-usage' },
        { label: 'Tickets', to: '/admin/ai-tickets' },
      ],
    },
  ];

  // Check if user has admin access
  const hasAdminAccess = user && ADMIN_ROLES.includes(user.role);

  if (!hasAdminAccess) {
    return (
      <div className="min-h-screen bg-[#0B1F18] flex items-center justify-center">
        <div className="text-center p-8 max-w-md">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/10 flex items-center justify-center">
            <Shield className="text-red-500" size={32} />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Access Denied</h1>
          <p className="text-slate-400 mb-6">
            This area is restricted to authorized administrators only.
          </p>
          <Button onClick={() => navigate('/dashboard')} variant="outline">
            Return to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B1F18]">
      {/* Sidebar */}
      <aside 
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-gradient-to-b from-[#0F2A21] via-[#0B1F18] to-[#081610] border-r border-[rgba(142,218,182,0.12)] transform transition-transform duration-300 ease-in-out flex flex-col
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
        data-testid="admin-sidebar"
      >
        {/* Logo / Header */}
        <div className="p-5 border-b border-[rgba(142,218,182,0.10)] flex-shrink-0">
          <div className="flex flex-col gap-1" data-testid="admin-sidebar-logo">
            <FundEliteBrandMark
              variant="agency-admin"
              mode="logo"
              logoClassName="h-8 w-auto max-w-[168px] object-contain object-left"
            />
            <p className="text-xs text-amber-400">Admin Control</p>
          </div>
        </div>

        {/* Security Notice */}
        <div className="px-4 py-3 border-b border-[rgba(142,218,182,0.08)] flex-shrink-0">
          <div className="flex items-center gap-2 text-xs text-amber-500/80">
            <Lock size={12} />
            <span>Internal Use Only</span>
          </div>
        </div>

        {/* Navigation - Scrollable */}
        <nav 
          className="flex-1 px-3 py-4 overflow-y-auto scrollbar-thin scrollbar-thumb-[rgba(142,218,182,0.20)] scrollbar-track-transparent"
        >
          {navItems.map((item, index) => (
            <NavItem
              key={index}
              icon={item.icon}
              label={item.label}
              to={item.to}
              children={item.children}
              badge={item.badge}
              openSection={openSection}
              onSectionToggle={setOpenSection}
            />
          ))}

          {/* Quick Return to Portal */}
          <div className="mt-6 pt-4 border-t border-stone-800">
            <Link
              to={user?.role === 'platform_owner' ? '/platform/dashboard' : '/staff/dashboard'}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm transition-all bg-stone-800/50 hover:bg-stone-800 border border-slate-700/50 hover:border-slate-600 group"
              data-testid="admin-return-portal"
            >
              <div className="p-1.5 rounded-md bg-slate-700 group-hover:bg-slate-600 transition-colors">
                <ArrowLeft size={16} className="text-slate-400 group-hover:text-white" />
              </div>
              <div className="flex-1">
                <span className="text-slate-300 group-hover:text-white block font-medium">Return to Portal</span>
                <span className="text-xs text-slate-500">
                  {user?.role === 'platform_owner' ? 'Platform Dashboard' : 'Staff Dashboard'}
                </span>
              </div>
            </Link>
          </div>
        </nav>

        {/* User Card - Fixed at bottom */}
        <div className="p-4 border-t border-stone-800 bg-stone-900/50 flex-shrink-0">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10 border-2 border-amber-500/30">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-amber-600/20 text-amber-400 font-semibold">
                {user?.name?.charAt(0) || 'A'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white truncate">{user?.name || 'Admin'}</p>
              <p className="text-xs text-slate-500 truncate capitalize">{user?.role?.replace('_', ' ')}</p>
            </div>
            <button
              onClick={handleLogout}
              className="text-slate-500 hover:text-white p-2 rounded-lg hover:bg-stone-800 transition-colors"
              data-testid="admin-logout-btn"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Menu Toggle */}
      <button
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        className="fixed top-4 left-4 z-50 lg:hidden p-2 rounded-lg bg-[rgba(10,34,25,0.85)] text-white border border-[rgba(142,218,182,0.12)]"
      >
        {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-[rgba(6,28,20,0.75)] z-30 lg:hidden backdrop-blur-sm"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Header */}
        <header className="sticky top-0 z-20 bg-[rgba(11,31,24,0.85)] backdrop-blur-md border-b border-[rgba(142,218,182,0.10)] px-4 lg:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 ml-12 lg:ml-0">
              <div>
                <h2 className="text-lg font-semibold text-white">Admin Control Center</h2>
                <p className="text-sm text-[#93AE9F]">Internal AI Operations & Configuration</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className={`hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full border ${
                systemStatus.overall_status === 'healthy' 
                  ? 'bg-emerald-500/10 border-emerald-500/20' 
                  : systemStatus.overall_status === 'degraded'
                    ? 'bg-amber-500/10 border-amber-500/20'
                    : 'bg-red-500/10 border-red-500/20'
              }`}>
                {systemStatus.overall_status === 'healthy' ? (
                  <>
                    <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                    <span className="text-xs text-emerald-300">All Systems Operational</span>
                  </>
                ) : systemStatus.overall_status === 'degraded' ? (
                  <>
                    <AlertTriangle size={12} className="text-amber-400" />
                    <span className="text-xs text-amber-300">System Degraded</span>
                  </>
                ) : (
                  <>
                    <div className="w-2 h-2 bg-slate-500 rounded-full"></div>
                    <span className="text-xs text-slate-400">Checking Status...</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-6 bg-[#0B1F18] min-h-[calc(100vh-73px)]">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
